import axios from 'axios';
import { linabase } from './linabase';

const API_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_API_URL || 'http://localhost:3001')
  : '';

/**
 * Generate a UUID v4 for client-side request tracing.
 * Uses crypto.randomUUID() when available (secure contexts),
 * falls back to manual generation for non-HTTPS environments.
 */
function generateClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts (e.g. HTTP in development/testing)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and request trace ID to requests
api.interceptors.request.use(async (config) => {
  // Add correlation ID for request tracing
  config.headers['X-Request-ID'] = generateClientRequestId();

  const {
    data: { session },
  } = await linabase.auth.getSession();

  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }

  return config;
});

// Handle errors
const PUBLIC_PATHS = ['/', '/pricing', '/status', '/privacy', '/terms'];
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Attach request ID from response headers for support reference
    const requestId = error.response?.headers?.['x-request-id']
      || error.config?.headers?.['X-Request-ID'];
    if (requestId) {
      error.requestId = requestId;
    }

    if (error.response?.status === 401) {
      const path = window.location.pathname;
      const isPublic = PUBLIC_PATHS.includes(path) || path.startsWith('/docs');
      if (!isPublic) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
